/* @jsx h */
import { computed, defineComponent, h, ref } from "vue";
import { LemmonModal, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import { Avatar } from "../../shared/Avatar";
import type { Member, Role, RotaSettings } from "../../Team/types";
import { shiftHours, warningsFor, weekHoursByPerson, type Absence, type PlannedShift } from "../engine/rules";
import type { Shift } from "./types";
import { warningText } from "./warningText";

type Props = {
  /** The shift to edit, or a new one on `date`. */
  shift: Shift | null;
  date: string;
  allShifts: Shift[];
  members: Member[];
  roles: Role[];
  settings: RotaSettings;
  absences: Absence[];
  onClose: (changed: boolean) => void;
};

// Pick who works a slot. Everyone is listed with this week's hours; anyone
// with a rule problem shows why (already working, short rest, no skill…) —
// rules only warn, so they can still be picked.
export const AssignModal = defineComponent<Props>({
  name: "AssignModal",
  props: ["shift", "date", "allShifts", "members", "roles", "settings", "absences", "onClose"],
  setup(props) {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();
    const s = props.shift;

    const start = ref(s?.start_time ?? "11:00");
    const end = ref(s?.end_time ?? "16:00");
    const breakMin = ref(s?.break_minutes ?? 0);
    const roleId = ref<number | null>(s?.role_id ?? props.roles[0]?.id ?? null);
    const staffId = ref<number | null>(s?.staff_id ?? null);
    const saving = ref(false);

    const draft = computed<PlannedShift>(() => ({
      id: s?.id ?? -1,
      shift_date: s?.shift_date ?? props.date,
      start_time: start.value,
      end_time: end.value,
      break_minutes: breakMin.value,
      role_id: roleId.value,
      staff_id: staffId.value,
    }));
    const week = computed(() => weekHoursByPerson(props.allShifts.filter((x) => x.id !== s?.id), draft.value.shift_date));

    const candidates = computed(() =>
      props.members
        .filter((m) => m.active)
        .map((m) => {
          const warnings = warningsFor(draft.value, m, props.allShifts, props.settings, props.absences);
          const busy = warnings.some((w) => w.kind === "overlap" || w.kind === "time_off");
          const skilled = roleId.value == null || m.role_ids.includes(roleId.value);
          return { m, warnings, busy, skilled, hours: week.value.get(m.id) ?? 0 };
        })
        .sort((a, b) => Number(a.busy) - Number(b.busy) || Number(b.skilled) - Number(a.skilled) || a.warnings.length - b.warnings.length || a.hours - b.hours),
    );

    const save = async () => {
      saving.value = true;
      try {
        await trpc.rota.shifts.save.mutate({
          id: s?.id,
          shift_date: draft.value.shift_date,
          start_time: start.value,
          end_time: end.value,
          break_minutes: breakMin.value,
          role_id: roleId.value,
          staff_id: staffId.value,
        });
        props.onClose(true);
      } catch (e) {
        toast.showToast((e as { message?: string })?.message ?? t("workday.common.save_failed"), { type: "error" });
      } finally {
        saving.value = false;
      }
    };

    const remove = async () => {
      if (!s || !confirm(t("workday.rota.delete_shift_confirm"))) return;
      await trpc.rota.shifts.delete.mutate({ id: s.id });
      props.onClose(true);
    };

    return () => {
      const dateLabel = new Date(`${draft.value.shift_date}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
      return (
        <LemmonModal
          title={s ? `${dateLabel}, ${start.value} – ${end.value}` : t("workday.rota.new_shift_on").replace("{date}", dateLabel)}
          onClose={() => props.onClose(false)}
          renderButtons={() => (
            <div class="border-t p-4">
              <div class="flex justify-between gap-2">
                <div class="flex gap-2">
                  <button class="btn btn-neutral" onClick={() => props.onClose(false)}>
                    {t("general.words.cancel")}
                  </button>
                  {s && (
                    <button class="btn btn-ghost text-error" onClick={remove}>
                      {t("workday.rota.delete_shift")}
                    </button>
                  )}
                </div>
                <button class="btn btn-primary" disabled={saving.value} onClick={save}>
                  {t("workday.common.save")}
                </button>
              </div>
            </div>
          )}
        >
          <div class="space-y-4 p-6">
            <div class="flex flex-wrap items-end gap-3">
              <label class="block">
                <span class="mb-1 block text-xs font-medium">{t("workday.rota.start")}</span>
                <input type="time" class="input input-bordered input-sm w-32" v-model={start.value} />
              </label>
              <label class="block">
                <span class="mb-1 block text-xs font-medium">{t("workday.rota.end")}</span>
                <input type="time" class="input input-bordered input-sm w-32" v-model={end.value} />
              </label>
              <label class="block">
                <span class="mb-1 block text-xs font-medium">{t("workday.rota.break")}</span>
                <input
                  type="number"
                  min="0"
                  step="5"
                  class="input input-bordered input-sm w-20"
                  value={breakMin.value}
                  onChange={(e: Event) => (breakMin.value = Number((e.target as HTMLInputElement).value) || 0)}
                />
              </label>
              <label class="block flex-1">
                <span class="mb-1 block text-xs font-medium">{t("workday.rota.role")}</span>
                <select
                  class="select select-bordered select-sm w-full"
                  value={roleId.value ?? ""}
                  onChange={(e: Event) => {
                    const v = (e.target as HTMLSelectElement).value;
                    roleId.value = v ? Number(v) : null;
                  }}
                >
                  <option value="">—</option>
                  {props.roles.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </label>
              <span class="pb-2 text-xs text-base-content/50">{`${shiftHours(draft.value).toFixed(1)} h`}</span>
            </div>

            <div class="rounded-xl border border-base-300">
              <button
                class={["flex w-full items-center gap-3 border-b border-base-300 px-3 py-2 text-left", staffId.value == null ? "bg-warning/15" : "hover:bg-base-200/60"].join(" ")}
                onClick={() => (staffId.value = null)}
              >
                <span class="flex h-8 w-8 items-center justify-center rounded-full border border-dashed border-warning text-warning">
                  <span class="material-symbols-outlined text-base">person_off</span>
                </span>
                <span class="flex-1 text-sm font-medium">{t("workday.rota.leave_open")}</span>
                {staffId.value == null && <span class="material-symbols-outlined text-success">check_circle</span>}
              </button>
              <ul class="max-h-80 divide-y divide-base-200 overflow-auto">
                {candidates.value.map(({ m, warnings, busy, skilled, hours }) => {
                  const picked = staffId.value === m.id;
                  const extra = picked ? 0 : shiftHours(draft.value);
                  return (
                    <li key={m.id}>
                      <button
                        class={["flex w-full items-center gap-3 px-3 py-2 text-left", picked ? "bg-success/10" : "hover:bg-base-200/60", busy && !picked ? "opacity-50" : ""].join(" ")}
                        onClick={() => (staffId.value = m.id)}
                      >
                        <Avatar name={m.name} id={m.id} color={m.color} size="sm" />
                        <span class="min-w-0 flex-1">
                          <span class="block text-sm font-medium">{m.name}</span>
                          <span class={["block truncate text-xs", warnings.length ? "text-warning" : "text-base-content/50"].join(" ")}>
                            {warnings.length
                              ? warnings.map((w) => warningText(w, t)).join(" · ")
                              : skilled
                                ? t("workday.rota.available")
                                : ""}
                          </span>
                        </span>
                        <span class="text-right text-xs tabular-nums text-base-content/60">
                          {`${(hours + extra).toFixed(1)}h`}
                          {m.contract_hours_week != null && <span class="text-base-content/40">{` / ${m.contract_hours_week}h`}</span>}
                        </span>
                        {picked && <span class="material-symbols-outlined text-success">check_circle</span>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </LemmonModal>
      );
    };
  },
});
